import React, { useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { SHEET_COLORS, SHEET_FONTS, SHEET_SIZES } from './sheetTheme'

export const SHEET_WIDTH_PX = 816
export const SHEET_MIN_HEIGHT_PX = 1056
const BACKDROP_PADDING_PX = 16

const Backdrop = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
  background: ${SHEET_COLORS.backdrop};
  padding: ${BACKDROP_PADDING_PX}px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`

const Frame = styled.div.attrs<{ $height: number; $scale: number }>(
  ({ $height, $scale }) => ({
    style: { width: SHEET_WIDTH_PX * $scale, height: $height * $scale },
  })
)`
  flex-shrink: 0;
`

const Paper = styled.div.attrs<{ $scale: number }>(({ $scale }) => ({
  style: { transform: `scale(${$scale})` },
}))`
  width: ${SHEET_WIDTH_PX}px;
  min-height: ${SHEET_MIN_HEIGHT_PX}px;
  box-sizing: border-box;
  padding: 0.55in 0.6in;
  background: ${SHEET_COLORS.paper};
  color: ${SHEET_COLORS.ink};
  border: 1px solid ${SHEET_COLORS.rule};
  box-shadow:
    0 1px 3px ${SHEET_COLORS.ink}1f,
    0 8px 24px ${SHEET_COLORS.ink}14;
  transform-origin: top left;
  font-family: ${SHEET_FONTS.sans};
  font-size: ${SHEET_SIZES.body}pt;
  line-height: 1.25;
  color-scheme: light;
  -webkit-font-smoothing: antialiased;
`

/** A white 8.5in sheet scaled down to fit its host; colors are fixed and ignore the app theme. */
export function DocumentSheet({
  children,
  testId,
}: {
  children: React.ReactNode
  testId?: string
}): React.ReactElement {
  const hostRef = useRef<HTMLDivElement>(null)
  const paperRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [paperHeight, setPaperHeight] = useState(SHEET_MIN_HEIGHT_PX)

  useLayoutEffect(() => {
    const host = hostRef.current
    const paper = paperRef.current
    if (!host || !paper || typeof ResizeObserver === 'undefined') return
    const update = () => {
      const available = host.clientWidth - 2 * BACKDROP_PADDING_PX
      setScale(available > 0 ? Math.min(1, available / SHEET_WIDTH_PX) : 1)
      setPaperHeight(paper.offsetHeight || SHEET_MIN_HEIGHT_PX)
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(host)
    observer.observe(paper)
    return () => observer.disconnect()
  }, [])

  return (
    <Backdrop ref={hostRef}>
      <Frame $height={paperHeight} $scale={scale}>
        <Paper ref={paperRef} data-testid={testId} $scale={scale}>
          {children}
        </Paper>
      </Frame>
    </Backdrop>
  )
}
